// components/NavigationUser.tsx
'use client'
import {
  Loader2,
  LogOut,
  MessageCircleWarning,
  TriangleAlert,
  Settings,
  Shield,
} from 'lucide-react'
import { useNavigate } from 'react-router'

import { useGetUserInfo } from '@/api/auth/useGetUserInfo'
import { useLogoutMutation } from '@/api/auth/useLogoutMutation'
import { toTitleCase } from '@/lib/utils'
import { authService } from '@/services/authService'

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar'

// Helper function to check if user is admin
const isAdminUser = (userInfo: any) => {
  return userInfo?.roles?.includes('ADMIN') || false
}

export function NavUser() {
  const { isMobile } = useSidebar()
  const navigate = useNavigate()

  // make an api call to fetch userInfo? data
  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    isError: isUserInfoError,
    isSuccess: isUserInfoSuccess,
  } = useGetUserInfo()

  // make an api call to logout
  const {
    mutateAsync: logoutMutateAsync,
    isLoading: isLoggingOut,
    isError: isLoggingOutError,
  } = useLogoutMutation({
    onSuccess: () => {
      authService
        .logout()
        .then(() => {
          navigate('/welcome?fromLogout=true')
          window.location.reload()
        })
        .catch((error) => {
          console.error(error)
        })
    },
    onError: (error) => {
      console.error(error)
    },
  })

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground md:h-8 md:p-0"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={userInfo?.name} alt={userInfo?.name} />
                <AvatarFallback className="rounded-lg">
                  {isUserInfoLoading && <Loader2 className="animate-spin" />}
                  {isUserInfoError && (
                    <TriangleAlert className="text-red-500" />
                  )}
                  {userInfo &&
                    isUserInfoSuccess &&
                    toTitleCase(userInfo?.name ?? '')?.substring(0, 2)}
                </AvatarFallback>
              </Avatar>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            {/* User Profile Section */}
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="grid flex-1 text-left text-sm leading-tight p-4 ml-2">
                <span className="truncate font-semibold mb-1">
                  {toTitleCase(userInfo?.name ?? '')}
                </span>
                <span className="truncate text-xs">{userInfo?.userName}</span>
                {/* Show roles with badges */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {userInfo?.roles?.map((role) => (
                    <span
                      key={role}
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        role === 'ADMIN'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Settings Option */}
            <DropdownMenuItem
              onClick={() => navigate('/settings')}
              className="cursor-pointer"
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>

            {/* Admin Console - Only show for admin users */}
            {isAdminUser(userInfo) && (
              <>
                <DropdownMenuItem
                  onClick={() => navigate('/admin/analytics')}
                  className="cursor-pointer"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Admin Console
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Logout Option */}
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault()
              }}
            >
              <Button
                className="w-full text-left justify-start p-0 h-auto font-normal"
                onClick={async () => {
                  console.log('logging out')
                  await logoutMutateAsync()
                  console.log('logged out')
                }}
                variant={'ghost'}
              >
                <div className="flex flex-1 space-x-2 items-center">
                  {isLoggingOut && <Loader2 className="animate-spin mr-2" />}
                  {isLoggingOutError && (
                    <MessageCircleWarning className="text-red-500 mr-2" />
                  )}
                  {!isLoggingOut && !isLoggingOutError && (
                    <LogOut className="mr-2 h-4 w-4" />
                  )}
                  Log out
                </div>
              </Button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
